import React, { useEffect } from "react";
import { Text, Divider } from "@fluentui/react-components";
import { useFormContext } from "react-hook-form";
import { enumValuesToOptions } from "../../../helper/orderHelper";
import { OrderFormData } from "../../../interfaces/OrderFormData";
import {
  SubscriptionLevel,
  packageDefaults,
} from "../../../interfaces/SubscriptionLevel";
import { Grid } from "../../common/grid/Grid";
import { GridItem } from "../../common/grid/GridItem";
import { ControlledAmountSelector } from "../../inputs/ControlledAmountSelector";
import { ControlledDropDown } from "../../inputs/ControlledDropdown";
import { ControlledOrganisationNumberInput } from "../../inputs/ControlledOrganisationNumberInput";
import { ControlledPhoneInput } from "../../inputs/ControlledPhoneInput";
import { ControlledTextInput } from "../../inputs/ControlledTextInput";
import { ControlledZipInput } from "../../inputs/ControlledZipInput";
import { t } from "../../../i18n/i18n";

export const OrderInformation = () => {
  const {
    control,
    watch,
    setValue,
  } = useFormContext<OrderFormData>();

  const subscriptionLevels = enumValuesToOptions(SubscriptionLevel).filter(
    (o) => o.key !== SubscriptionLevel.CUSTOM
  );

  const subscription = watch("subscription");

  // When a named package is selected, auto-fill users/workplaces from its defaults
  useEffect(() => {
    if (!subscription || subscription === SubscriptionLevel.CUSTOM) return;
    const defaults = packageDefaults[subscription as SubscriptionLevel];
    if (!defaults) return;
    setValue("numberOfUsers", defaults.users);
    setValue("numberOfWorkplaces", defaults.workplaces);
  }, [subscription, setValue]);

  return (
    <>
      <Grid>
        <GridItem size="12/12">
          <Text size={600} weight="bold">
            {t("SubscriptionDetails")}
          </Text>
        </GridItem>
        <GridItem size="6/12">
          <ControlledDropDown
            required
            control={control}
            name={"subscription"}
            label={t("Subscription")}
            options={subscriptionLevels}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledAmountSelector
            required
            control={control}
            name={"numberOfUsers"}
            label={t("NumberOfUsers")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledAmountSelector
            required
            control={control}
            name={"numberOfWorkplaces"}
            label={t("NumberOfWorkplaces")}
          />
        </GridItem>

        <GridItem size="12/12">
          <Divider appearance="strong" />
        </GridItem>
        <GridItem size="12/12">
          <Text size={600} weight="bold">
            {t("OrganisationDetails")}
          </Text>
        </GridItem>

        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"organisation.organisationName"}
            label={t("OrganisationName")}
            placeholder={t("OrganisationNamePlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledOrganisationNumberInput
            required
            control={control}
            name={"organisation.organisationNumber"}
            label={t("OrganisationNumber")}
            placeholder={t("OrganisationNumberPlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"organisation.organisationType"}
            label={t("OrganisationType")}
            placeholder={t("OrganisationTypePlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"organisation.snis"}
            label={t("Snis")}
            placeholder={t("SnisPlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledAmountSelector
            required
            control={control}
            name={"numberOfEmployees"}
            label={t("NumberOfEmployees")}
          />
        </GridItem>

        <GridItem size="12/12">
          <Divider appearance="strong" />
        </GridItem>
        <GridItem size="12/12">
          <Text size={600} weight="bold">
            {t("InvoiceDetails")}
          </Text>
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"invoice.invoiceName"}
            label={t("InvoiceName")}
            placeholder={t("InvoiceNamePlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"invoice.invoiceReference"}
            label={t("InvoiceReference")}
            placeholder={t("InvoiceReferencePlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"invoice.invoiceAddress"}
            label={t("InvoiceAddress")}
            placeholder={t("InvoiceAddressPlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledZipInput
            required
            control={control}
            name={"invoice.postalCode"}
            label={t("PostalCode")}
            placeholder={t("PostalCodePlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="text"
            name={"invoice.county"}
            label={t("County")}
            placeholder={t("CountyPlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledPhoneInput
            required
            control={control}
            name={"invoice.phoneNumber"}
            label={t("PhoneNumber")}
            placeholder={t("PhoneNumberPlaceholder")}
          />
        </GridItem>
        <GridItem size="6/12">
          <ControlledTextInput
            required
            control={control}
            type="email"
            name={"invoice.email"}
            label={t("Email")}
            placeholder={t("EmailPlaceholder")}
          />
        </GridItem>
        <GridItem size="12/12">
          <ControlledTextInput
            multiline
            control={control}
            type="text"
            name={"invoice.otherInvoiceDetails"}
            label={t("OtherInvoiceDetails")}
            placeholder={t("OtherInvoiceDetailsPlaceholder")}
          />
        </GridItem>
      </Grid>
    </>
  );
};
